import * as React from 'react';
import {FunctionComponent, useMemo} from 'react';
import {PlainGraph} from './PlainGraph';
import {Stroke} from '../Models/Stroke';
import {Speed} from '../Domain/Speed';
import {formatSplit} from '../Ui/Support/Formatting/formatSplit';
import {formatDuration} from '../Ui/Support/Formatting/formatDuration';
import {formatDistance} from '../Ui/Support/Formatting/formatDistance';
import {Duration} from '../Domain/Duration';
import {getXAxisUnitInnerProperty, XAxisUnit} from './support/variableXAxis';
import {lazyTake, strokesToGraphableStrokes, strokesToGraphData} from './support/strokesToGraphData';

interface OwnProps {
    strokes: Stroke[];
    xAxisUnit: XAxisUnit;
    tooltip?: {point: {x: number; y: number; label?: string}; x: number} | null;
    onDataActivated?: (data: {point: {x: number; y: number; label?: string}; x: number}) => void;
}

export const SplitGraph: FunctionComponent<OwnProps> = ({strokes, xAxisUnit, tooltip, onDataActivated}) => {
    if (strokes.length === 0) {
        return <div className="graphs__empty">No stroke data available</div>;
    }

    // Cap max speed to 45 seconds per 500m.
    const speedCap = Speed.fromDurationPer500Meters(Duration.fromSeconds(45))!.metersPerSecond;
    const cappedStrokes = strokes.map((s) => {
        const speed: number = s.speed?.metersPerSecond === undefined ? 0 : s.speed?.metersPerSecond;
        return {...s, speed: Speed.fromMetersPerSecond(speed > speedCap ? speedCap : speed)};
    });

    // Create axis and values from strokes.
    const graphableStrokes = useMemo(() => strokesToGraphableStrokes(cappedStrokes), [strokes]);
    const graphData = useMemo(() => {
        return strokesToGraphData(
            graphableStrokes,
            xAxisUnit,
            (stroke) => (stroke.speed ? stroke.speed.metersPerSecond : 0),
            (speed) => formatSplit(Speed.fromMetersPerSecond(speed)).formatted,
        );
    }, [strokes, xAxisUnit]);
    const tooltipStroke = graphableStrokes.find(
        (stroke) => lazyTake(stroke, getXAxisUnitInnerProperty(xAxisUnit)) === tooltip?.point.x,
    );

    // Flip graph data.
    const flipped = {
        axis: {
            x: graphData.axis.x,
            y: graphData.axis.y.map((y) => {
                return {label: y.label, value: y.value};
            }),
        },
        values: graphData.values.map((v) => {
            return {x: v.x, y: v.y};
        }),
    };
    return (
        <PlainGraph
            height={80}
            padding={{top: 0, right: 0, bottom: 20, left: 20}}
            axis={flipped.axis}
            values={flipped.values}
            tooltip={
                tooltip &&
                tooltipStroke && {
                    x: tooltip.x,
                    content: [
                        '/500m: ' + formatSplit(tooltipStroke?.speed ?? null).formatted,
                        'Time: ' + formatDuration(tooltipStroke?.totalTime ?? null).formatted,
                        'Dist: ' +
                            formatDistance(tooltipStroke?.totalDistance ?? null).formatted +
                            ' ' +
                            formatDistance(tooltipStroke?.totalDistance ?? null).unit,
                    ].join('\n'),
                }
            }
            onDataActivated={onDataActivated}
        />
    );
};
